import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';

import Button from 'components/shared/Button';
import Icon from 'components/shared/Icon';

import { handleKeyEvents } from 'utils/handlers';

type MenuProps = {
  triggerText: string;
  menuItems: JSX.Element[];
  triggerId: string;
  className?: string;
  triggerIcon?: IconProp | null;
  triggerIconSize?: SizeProp;
  iconOnlyTrigger?: boolean;
  direction?: 'up' | 'down';
  position?: 'left' | 'right';
  appearance?: 'dark' | 'light' | 'themed';
  disabled?: boolean;
};

function Menu({
  direction = 'down',
  position = 'left',
  triggerIcon = null,
  triggerIconSize = 'lg',
  iconOnlyTrigger = false,
  className = '',
  appearance = 'dark',
  disabled = false,
  triggerId,
  triggerText,
  menuItems,
}: MenuProps) {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  function handleTriggerClick() {
    setMenuIsOpen((prev) => !prev);
  }

  const handleArrowUp = useCallback(() => {
    if (menuIsOpen && menuRef.current) {
      const listItems: NodeListOf<HTMLButtonElement> =
        menuRef.current.querySelectorAll('li button');
      listItems[listItems.length - 1].focus();
    } else {
      setMenuIsOpen(true);
    }
  }, [menuIsOpen]);

  const handleArrowDown = useCallback(() => {
    if (menuIsOpen) {
      setMenuIsOpen(false);
    }
  }, [menuIsOpen]);

  const handleArrowRight = useCallback(() => {
    if (!menuIsOpen) {
      setMenuIsOpen(true);
    }
  }, [menuIsOpen]);

  const handleArrowLeft = useCallback(() => {
    if (menuIsOpen) {
      setMenuIsOpen(false);
    }
  }, [menuIsOpen]);

  const handleArrowKeys = useCallback(
    (e: React.KeyboardEvent) => {
      const { key } = e;
      e.preventDefault();
      switch (key) {
        case 'ArrowUp':
          handleArrowUp();
          break;
        case 'ArrowDown':
          handleArrowDown();
          break;
        case 'ArrowRight':
          handleArrowRight();
          break;
        case 'ArrowLeft':
          handleArrowLeft();
          break;
        default:
          break;
      }
    },
    [handleArrowDown, handleArrowLeft, handleArrowRight, handleArrowUp]
  );

  function handleOffClickClose(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (menuRef.current && menuRef.current.contains && !menuRef.current.contains(target)) {
      setMenuIsOpen(false);
    }
  }

  const caretDirection = useMemo(() => {
    switch (direction) {
      case 'up':
        return menuIsOpen ? 'down' : 'up';
      case 'down':
        return menuIsOpen ? 'up' : 'down';
    }
  }, [direction, menuIsOpen]);

  const triggerAppearance = useMemo(() => {
    switch (appearance) {
      case 'dark':
        return 'light';
      case 'light':
        return 'dark';
      case 'themed':
        return 'themed';
    }
  }, [appearance]);

  const icon = useMemo(() => {
    if (!triggerIcon) {
      return null;
    }

    return (
      <Icon className="menu-trigger-icon" icon={triggerIcon as IconProp} size={triggerIconSize} />
    );
  }, [triggerIcon, triggerIconSize]);

  useEffect(() => {
    if (menuIsOpen) {
      document.addEventListener('click', handleOffClickClose);
    } else {
      document.removeEventListener('click', handleOffClickClose);
    }

    return () => {
      document.removeEventListener('click', handleOffClickClose);
    };
  }, [menuIsOpen]);

  return (
    <div
      ref={menuRef}
      className={`menu-container ${appearance} ${className} ${menuIsOpen ? 'open' : ''}`}
    >
      <Button
        type={iconOnlyTrigger ? 'icon' : 'cornered'}
        icon={iconOnlyTrigger && triggerIcon ? triggerIcon : undefined}
        id={triggerId && triggerId}
        aria-controls={`${triggerId}-menu`}
        aria-expanded={menuIsOpen}
        className="menu-trigger"
        a11yText={triggerText}
        appearance={triggerAppearance}
        size="lg"
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyEvents({ open: () => false, navigate: handleArrowKeys })}
      >
        {!iconOnlyTrigger && triggerIcon && icon}
        {!iconOnlyTrigger && triggerText}
        <FontAwesomeIcon className="menu-trigger-caret" icon={`chevron-${caretDirection}`} />
      </Button>

      <ul
        role="menu"
        id={`${triggerId}-menu`}
        aria-labelledby={triggerId}
        className={`menu-items ${position} ${direction}`}
      >
        {menuItems.map((item, index) => (
          <li
            // FIXME: these should probably be objects with nodes as property
            // eslint-disable-next-line react/no-array-index-key
            key={`${triggerText}-${index}`}
            className={`menu-item ${item.props.disabled ? 'disabled' : ''}`}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Menu;
